/**
 * Stage 1A — gram pack-to-portion bridge (2026-11).
 *
 * Shown when a mass (g/kg) product has a smaller "portion" Sales unit
 * subdividing its Pack (stock/count) unit — e.g. a 500g pack portioned
 * into 15g servings. Purely a computed summary over the existing Units
 * table; it introduces NO new persisted field except the portion row's
 * existing (already generic) `observed_servings_per_unit` — reused here
 * as "observed yield", exactly like a volume product's Shot/Bottle yield.
 *
 * Nominal yield and remainder are always derived live from the two unit
 * rows' own grams, never stored separately, so they can never drift out
 * of sync with the units themselves. Never shown for count (packet, sold
 * whole) or volume products, and never mixes a kg/g product with a
 * ml-denominated one — fails closed (hidden) on anything ambiguous.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Package } from 'lucide-react';
import { measureTypeOf } from './measureType';

function toGrams(unit) {
  if (!unit || !unit.conversion_to_base) return null;
  const v = parseFloat(unit.conversion_to_base);
  if (!v || v <= 0) return null;
  return (unit.base_unit || '').toLowerCase() === 'kg' ? v * 1000 : v;
}

export function PortionYieldPanel({ form, setForm }) {
  const units = form.units || [];
  const massUnits = units.filter(u => measureTypeOf(u.base_unit) === 'mass' && u.unit_type && u.conversion_to_base);
  if (massUnits.length < 2) return null; // need at least a pack AND a portion

  // Pack = the stock/count unit (mirrors stockUnitIndex convention: base
  // stock unit, else default count unit) — must be mass-typed.
  const packUnit = massUnits.find(u => u.is_base_stock_unit) || massUnits.find(u => u.is_default_count_unit);
  if (!packUnit) return null;
  const packGrams = toGrams(packUnit);
  if (!packGrams) return null;

  // Portion = the smallest Sales unit that genuinely subdivides the pack
  // (strictly smaller than the pack itself — a unit equal to the pack is
  // "sold whole", the existing packaged-snack count pattern, not this one).
  const portionCandidates = massUnits
    .filter(u => u.is_sales_unit && u !== packUnit)
    .map(u => ({ unit: u, grams: toGrams(u) }))
    .filter(x => x.grams && x.grams < packGrams);
  if (portionCandidates.length === 0) return null;
  portionCandidates.sort((a, b) => a.grams - b.grams);
  const { unit: portionUnit, grams: portionGrams } = portionCandidates[0];

  const portionIdx = units.findIndex(u => u === portionUnit);
  const caseUnit = massUnits.find(u => u.is_default_purchase_unit && u !== packUnit && toGrams(u) > packGrams);
  const caseGrams = caseUnit ? toGrams(caseUnit) : null;

  const nominalYield = Math.floor(packGrams / portionGrams);
  const remainderG = +(packGrams - nominalYield * portionGrams).toFixed(4);
  const observedYield = portionUnit.observed_servings_per_unit;

  const updateObserved = (val) => setForm(f => ({
    ...f, units: f.units.map((u, i) => (i === portionIdx ? { ...u, observed_servings_per_unit: val } : u)),
  }));

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 space-y-2.5" data-testid="portion-yield-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-900 uppercase tracking-wide">
        <Package className="h-3.5 w-3.5" /> Pack-to-portion yield
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <p className="text-[11px] text-slate-500">Pack quantity</p>
          <p className="text-sm font-semibold text-slate-800" data-testid="portion-yield-pack-label">
            1 {packUnit.unit_type}{caseUnit && ` (${caseUnit.unit_type} = ${(caseGrams / packGrams).toFixed(0)} packs)`}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-slate-500">Grams per pack</p>
          <p className="text-sm font-semibold text-slate-800" data-testid="portion-yield-pack-grams">{packGrams} g</p>
        </div>
        <div>
          <p className="text-[11px] text-slate-500">Grams per portion</p>
          <p className="text-sm font-semibold text-slate-800" data-testid="portion-yield-portion-grams">{portionGrams} g ({portionUnit.unit_type})</p>
        </div>
        <div>
          <p className="text-[11px] text-slate-500">Nominal yield &amp; remainder</p>
          <p className="text-sm font-semibold text-slate-800" data-testid="portion-yield-nominal">
            {nominalYield} portions{remainderG > 0 && <span className="font-normal text-slate-500"> + {remainderG} g left over</span>}
          </p>
        </div>
      </div>
      <div className="flex items-end gap-2 pt-1">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-700" htmlFor="portion-yield-observed">
            Observed yield (optional)
          </label>
          <Input id="portion-yield-observed" type="number" min="0" step="0.1" className="h-8 text-xs w-28"
            value={observedYield ?? ''} placeholder={String(nominalYield)}
            onChange={(e) => updateObserved(e.target.value)}
            data-testid="portion-yield-observed" />
        </div>
        <p className="text-[11px] text-slate-500 pb-1.5">
          Portions actually counted from a real pack, if different from the nominal {nominalYield} above (e.g. wastage or over-portioning). Leave blank to use nominal.
        </p>
      </div>
      <p className="text-[11px] text-slate-500">
        Computed from the Units table above — never stored separately, never treated as millilitres.
      </p>
    </div>
  );
}
